import { memo, useCallback, useEffect, useRef } from 'react'
import { AlertTriangle, Bell, Check, Info, Trash2, X } from 'lucide-react'
import { useNoticeCenterOpen, useNoticeHistory, useStore, useUnreadNoticeCount } from '../../store/index.js'
import type { NoticeEntry } from '../../lib/notifications.js'
import './FeedbackOverlays.css'

const ICONS: Record<NoticeEntry['severity'], typeof Check> = {
  success: Check,
  error:   X,
  warning: AlertTriangle,
  info:    Info,
}

const SEVERITY_LABELS: Record<NoticeEntry['severity'], string> = {
  success: 'Succès',
  error:   'Erreur',
  warning: 'Avertissement',
  info:    'Information',
}

function formatNoticeTime(iso: string, now = new Date()): string {
  const date = new Date(iso)
  const time = date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  if (date.toDateString() === now.toDateString()) return time
  return `${date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} ${time}`
}

const NoticeRow = memo(function NoticeRow({ entry }: { entry: NoticeEntry }) {
  const Icon = ICONS[entry.severity]
  const handleFocus = useCallback(() => {
    if (!entry.focus) return
    const store = useStore.getState()
    store.focusAgendaItem(entry.focus)
    store.setNoticeCenterOpen(false)
  }, [entry.focus])

  return (
    <li className={`tp-notice is-${entry.severity}`}>
      <Icon size={13} aria-label={SEVERITY_LABELS[entry.severity]} className="tp-notice__icon" />
      <time className="tp-notice__time" dateTime={entry.at}>{formatNoticeTime(entry.at)}</time>
      <span className="tp-notice__message">
        {entry.message}
        {entry.count > 1 && <span className="tp-notice__count"> ×{entry.count}</span>}
      </span>
      {entry.focus && (
        <button type="button" className="tp-notice__action" onClick={handleFocus}>Voir</button>
      )}
    </li>
  )
})

/**
 * Cloche de la barre d'application et son historique. L'historique est
 * persistant (localStorage, `lib/notifications.ts`) : ce qu'une carte de la
 * pile n'a affiché que quelques secondes reste lisible en entier ici, même
 * après un rechargement.
 */
export const NotificationCenter = memo(function NotificationCenter() {
  const open = useNoticeCenterOpen()
  const history = useNoticeHistory()
  const unread = useUnreadNoticeCount()
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const toggle = useCallback(() => {
    const store = useStore.getState()
    store.setNoticeCenterOpen(!store.noticeCenterOpen)
  }, [])
  const close = useCallback(() => useStore.getState().setNoticeCenterOpen(false), [])
  const clear = useCallback(() => useStore.getState().clearNoticeHistory(), [])

  useEffect(() => {
    if (!open) return
    const onPointer = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) close()
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      close()
      triggerRef.current?.focus()
    }
    document.addEventListener('pointerdown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [close, open])

  return (
    <div className="tp-notice-center" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={`tp-notice-center__trigger${unread > 0 ? ' has-unread' : ''}`}
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={unread > 0 ? `Notifications, ${unread} non lue(s)` : 'Notifications'}
        title="Historique des notifications"
      >
        <Bell size={15} aria-hidden="true" />
        {unread > 0 && <span className="tp-notice-center__badge" aria-hidden="true">{unread > 99 ? '99+' : unread}</span>}
      </button>

      {open && (
        <div className="tp-notice-center__panel" role="dialog" aria-label="Historique des notifications">
          <header className="tp-notice-center__header">
            <strong>Notifications</strong>
            <button
              type="button"
              className="tp-notice-center__clear"
              onClick={clear}
              disabled={history.length === 0}
            >
              <Trash2 size={12} aria-hidden="true" />
              Tout effacer
            </button>
          </header>
          {history.length === 0 ? (
            <p className="tp-notice-center__empty">Aucune notification.</p>
          ) : (
            <ul className="tp-notice-center__list">
              {history.map(entry => <NoticeRow key={entry.id} entry={entry} />)}
            </ul>
          )}
        </div>
      )}
    </div>
  )
})
